import { getSearchArticles } from 'packages/mybridgev4/store/actions/global.data.actions';
import { useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HStack, VStack, Box, Stack } from '@mybridge/ui/layout';
import { Avatar } from '@mybridge/ui/avatar';
import { Text } from '@mybridge/ui/text';
import { Tooltip, useDisclosure } from '@chakra-ui/react';
import { Menu, MenuButton, MenuList, MenuItem } from '@mybridge/ui/menu';
import { copyTextToClipboard, getDomainName } from 'v4/lib/commons';
import { IconButton } from '@mybridge/ui/icon-button';
import {
  BookMarkPostIcon,
  BookMarkedPostIcon,
  EditIcon,
  Report,
  SharedConnectionIcon,
  VerticalThreeDot,
} from '@mybridge/icons';
import { PostBoxBlockReportModal } from 'v4/components/post/post-actions-modals/block-report';
import Link from 'next/link';
import {
  blogDetailsSlug,
  bookMarkPost,
  removeBookMark,
} from 'packages/mybridgev4/store/slices/blogSlice';
import { useToast } from '@mybridge/ui';
import moment from 'moment';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { RepostIcon } from '@mybridge/icons/Repost';
import { QuoteIcon } from '@mybridge/icons/Quote';
import { CopyIcon } from '@mybridge/icons/Copy';
import { PostBoxContext } from 'v4/components/post/context';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getMyArticlesFunc } from 'v4/store/actions/post.actions';
import { getNextPageFromURL } from 'v4/utils/utils';
import 'moment/min/locales';

const ArticleUserDetailsRight = ({ blogDetails, ...props }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslator();
  const blockReportModal = useDisclosure();
  const { userArticlesListCount } = useSelector((state) => state.globalData);
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const { loggedIn } = useSelector((state) => state.user);
  const [articleCount, setArticleCount] = useState(0);
  const [eventAdmin, setEventAdmin] = useState(false);

  const currentLanguage = useSelector((state) => state.translation.language) || 'en';
  useEffect(() => {
    if (currentLanguage === 'zh-cn') {
      moment.locale('zh-cn'); // Default to Simplified Chinese
    } else {
      moment.locale(currentLanguage);
    }
  }, [currentLanguage]);

  useEffect(() => {
    dispatch(getSearchArticles({ member_id: blogDetails?.author?.id }));
    if (blogDetails?.author?.id === userProfileInfo?.id) {
      setEventAdmin(true);
    } else {
      setEventAdmin(false); // Ensure this case is handled
    }
  }, [blogDetails, userProfileInfo]);

  useEffect(() => {
    if (userArticlesListCount > 0) {
      setArticleCount(userArticlesListCount?.toString());
    }
  }, [userArticlesListCount]);


  const copyPostLink = () => {
    if (typeof window === 'undefined') return;
    copyTextToClipboard(
      `${getDomainName()}/articles/detail/${blogDetails?.id}`
    );
    toast?.({
      title: t('myDisk.linkCopiedSuccessfully') || 'Link copied successfully!',
      status: 'success',
      position: 'top',
    });
  };
  const {
    data: myArticles,
    isFetching: articlesListFetching,
    isLoading: articlesListLoading,
    refetch: refetchingArticles,
    fetchNextPage: isFetchingNextPage,
    hasNextPage: fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['getMyArticlesFunc', { status: 2, include_bookmarks: true }],
    queryFn: async ({ pageParam, queryKey }) => {
      const resp = (
        await dispatch(
          getMyArticlesFunc({ pageParam, status: queryKey[1]?.status, include_bookmarks: queryKey[1]?.include_bookmarks })
        )
      )?.payload;
      return resp;
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
  });
  const handleBlogBookMark = (blogDetailsId) => {
    dispatch(bookMarkPost(blogDetailsId)).then((res) => {
      if (res.payload.readers) {
        toast?.({
          title: t('toast.articleBookmarked') || 'Article bookmarked!',
          status: 'success',
          position: 'top',
        });
      }
      
      // Refetch the articles list to update bookmarks
      refetchingArticles();
      
      // Fetch the updated blog details with the correct slug
      if (blogDetails?.slug) {
        dispatch(blogDetailsSlug(blogDetails.slug));
      }
      
      // Force update the UI by updating the local state
      // This is a workaround if the Redux state isn't updating the UI
      if (userProfileInfo) {
        const updatedBookmarkIds = blogDetails.bookmark_user_ids || [];
        const userIdIndex = updatedBookmarkIds.indexOf(userProfileInfo.id);
        
        // Toggle the bookmark status locally
        if (userIdIndex === -1) {
          updatedBookmarkIds.push(userProfileInfo.id);
        } else {
          updatedBookmarkIds.splice(userIdIndex, 1);
        }
      }
    });
  };

  const handleRemoveBlogBookMark = (blogDetailsId) => {
    dispatch(removeBookMark(blogDetailsId)).then((res) => {
      if (res.payload.readers) {
        toast?.({
          title: 'Bookmark removed!',
          status: 'success',
          position: 'top',
        });
      }
      
      // Refetch the articles list to update bookmarks
      refetchingArticles();
      
      // Fetch the updated blog details with the correct slug
      if (blogDetails?.slug) {
        dispatch(blogDetailsSlug(blogDetails.slug));
      }
      
      // Force update the UI by updating the local state
      // This is a workaround if the Redux state isn't updating the UI
      if (userProfileInfo) {
        const updatedBookmarkIds = blogDetails.bookmark_user_ids || [];
        const userIdIndex = updatedBookmarkIds.indexOf(userProfileInfo.id);
        
        // Toggle the bookmark status locally
        if (userIdIndex === -1) {
          updatedBookmarkIds.push(userProfileInfo.id);
        } else {
          updatedBookmarkIds.splice(userIdIndex, 1);
        }
      }
    });
  };

  const isCurrentUserAuthor = blogDetails?.company?.id && blogDetails?.author?.id === userProfileInfo?.id;

  const avatarLink = isCurrentUserAuthor
    ? `/page/private/${blogDetails?.company?.id}/`
    : blogDetails?.company?.id
    ? `/c/${blogDetails?.company?.slug}`
    : `/p/${blogDetails?.author?.slug}`;

  return (
    <>
      <HStack justifyContent="space-between" {...props} alignItems="flex-start">
        <HStack gap={3} alignItems={'flex-start'}>
          <Avatar
            src={blogDetails?.company?.id ? blogDetails.company.image : blogDetails?.author?.profile_pic}
            name={blogDetails?.company?.id ? blogDetails.company.name : blogDetails?.author?.display_name}
            size="lg"
            {...(avatarLink ? { as: Link, href: avatarLink } : {})}
          />

          <VStack gap={0} alignItems="flex-start">
          <Text
            color="brandPrimary.500"
            fontSize="20px"
            fontWeight="semibold"
            {...(avatarLink ? { as: Link, href: avatarLink } : {})}
          >
            {blogDetails?.company?.id
              ? blogDetails?.company.name
              : `${blogDetails?.author?.display_name}${blogDetails?.company?.name ? ' | ' + blogDetails?.company.name : ''}`}
          </Text>
            <HStack w="100%" gap={2} justifyContent="flex-start">
              <Text>
                {moment(blogDetails?.created_at).format('MMM D, YYYY')}
              </Text>
            </HStack>
            <Text color="brandGray.30" fontSize="16px" fontWeight="normal">
              {blogDetails?.company?.id ? (
                (blogDetails?.company?.company_type&&blogDetails?.company?.company_type)+(blogDetails?.company?.main_industry && ', ' + blogDetails?.company?.main_industry)
              ) : blogDetails?.author?.company && (
                `${blogDetails?.author?.position} ${blogDetails?.author?.company && "at " + blogDetails?.author?.company}`
              )}
            </Text>
            <Text color="brandGray.850" fontSize="14px" fontWeight="normal">
              {blogDetails?.company?.id ? (blogDetails?.company?.city ? (blogDetails?.company?.city + ', ' + blogDetails?.company?.country) : blogDetails?.company?.country) : blogDetails?.author?.location ?? blogDetails?.author?.company}
            </Text>
          </VStack>
        </HStack>
        <HStack gap={3}>
          {eventAdmin && (
            <Link href={`/articles/create/${blogDetails?.id}`}>
              <IconButton
                icon={<EditIcon color="black" />}
                variant="transparent"
              />
            </Link>
          )}
          {!eventAdmin && (
            <>
              <Link href={`/p/${blogDetails?.author?.slug}?tab=articles`}>
                <Box whiteSpace="nowrap">
                  {articleCount} {articleCount == 1 ? (t('createPost.article') || 'Article') : (t('navigation.articles') || 'Articles')}
                </Box>
              </Link>
              {loggedIn && (
                <>
                  {userProfileInfo &&
                  blogDetails?.bookmark_user_ids?.includes(
                    userProfileInfo?.id
                  ) ? (
                    <Tooltip label={'Remove Bookmark'}>
                      <IconButton
                        variant="secondary"
                        icon={<BookMarkedPostIcon />}
                        onClick={() => handleRemoveBlogBookMark(blogDetails?.id)}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip label={'Bookmark'}>
                      <IconButton
                        variant="secondary"
                        icon={<BookMarkPostIcon />}
                        onClick={() => handleBlogBookMark(blogDetails?.id)}
                      />
                    </Tooltip>
                  )}
                  <Menu>
                    <MenuButton>
                      <VerticalThreeDot />
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        icon={<CopyIcon width={20} height={20} />}
                        onClick={() => copyPostLink()}
                      >
                        {t('post.copyLink') || "Copy Link"}
                      </MenuItem>
                      {/* <MenuItem
                        onClick={(e) => repostModal?.onOpen?.()}
                        icon={<RepostIcon width={20} height={20} />}
                      >
                        {t('post.repost') || "Repost"}
                      </MenuItem>
                      <MenuItem
                        onClick={(e) => {quotePostModal?.onOpen?.()}}
                        icon={<QuoteIcon width={20} height={20} />}
                      >
                        {t('post.quotePost') || "Quote Post"}
                      </MenuItem> */}
                      <MenuItem
                        icon={<Report width="18" height="18" />}
                        onClick={() => {
                          blockReportModal.onOpen();
                        }}
                      >
                        {t('profileNetwork.report') || 'Report'}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </>
              )}
            </>
          )}
        </HStack>
      </HStack>
      {blockReportModal?.isOpen ? (
        <PostBoxBlockReportModal {...blockReportModal} text={"article"} />
      ) : null}
    </>
  );
};

export default ArticleUserDetailsRight;
